import { useState } from 'react';

import { CloudArrowUpIcon, PhotoIcon } from '@heroicons/react/24/solid';
import { ErrorAlert, SuccessAlert } from '../../components/alerts';
import {
  createAppAboutUsItem,
  getSingleUploadPresignedS3Url,
  uploadToS3PresignedUrl,
} from '../../api/new-api';
import { useNavigate } from 'react-router';
import { PELinearProgressWithLabel } from '../../components/linear-progress';
import Iphone from '../../components/iphone';
import { useColors } from '../../provider/colors-provider';
import CustomInput from '../../components/CustomInput';

const LinkCreator = () => {
  const { primary } = useColors();
  const navigate = useNavigate();

  const [data, setData] = useState<any>({
    displayText: '',
    displayLinkUrl: '',
    attachmentType: 'WEB_URL',
    imageKey: '',
  });
  const [file, setFile] = useState<null | File>(null);
  const [blobUrl, setBlobUrl] = useState<null | string>(null);
  const [error, setError] = useState<any>('');
  const [success, setSuccess] = useState<any>('');
  const [loading, setLoading] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);

  const handleFileChange = (event: { target: { files: any[] } }) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
      setData((prevData: any) => ({
        ...prevData,
        attachmentType: 'FILE_UPLOAD',
        newItemUrl: '',
      }));
      setError('');
      const blobUrl = URL.createObjectURL(file);
      setBlobUrl(blobUrl);

      const img = new Image();
      img.src = blobUrl;
    }
  };

  const validate = () => {
    if (data.attachmentType === 'FILE_UPLOAD' && !file) {
      setError('Please upload a file');
      return true;
    }

    if (!data.displayText) {
      setError('Please enter display text');
      return true;
    }

    if (data.attachmentType === 'WEB_URL' && !data.displayLinkUrl) {
      setError('Please enter a link url');
      return true;
    }

    setError('');
    return false;
  };

  const handleSubmit = async () => {
    const hasErrors = validate();
    if (hasErrors) {
      return;
    }

    try {
      setLoading(true);
      setPercentCompleted(2);

      if (data.attachmentType === 'WEB_URL' && !file) {
        await createAppAboutUsItem({
          ...data,
        });
        setPercentCompleted(100);
        setSuccess(true);
        setTimeout(() => {
          navigate('/mobile-about-us');
        }, 1500);
        return;
      }

      const presignedUrlResponse = await getSingleUploadPresignedS3Url({
        imageFileName: file!.name,
      });

      if (presignedUrlResponse && presignedUrlResponse.data) {
        const uploadUrl = presignedUrlResponse.data.imageUploadUrl;
        const mediaS3Key = presignedUrlResponse.data.imageKey;

        // Step 2: Upload the file to S3 using the presigned URL
        await uploadToS3PresignedUrl({
          uploadUrl,
          data: file,
          progressCallback: (percentCompleted) => {
            console.log(`Upload progress: ${percentCompleted}%`);
            setPercentCompleted(percentCompleted);
          },
        });
        // step 3: create the link
        await createAppAboutUsItem({
          ...data,
          imageKey: mediaS3Key,
        });

        setPercentCompleted(100);
        setSuccess(true);
        setTimeout(() => {
          navigate('/mobile-about-us');
        }, 1500);
      }
    } catch (error) {
      console.error('An error occurred:', error);
      setLoading(false);
      setError('An error occurred. Please refresh browser and try again.');
    }
  };

  return (
    <div className="w-full">
      <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
        <div className={'flex flex-row gap-6 justify-center'}>
          <div className="w-full flex justify-center">
            <Iphone previewItems={[data.displayText]} />
          </div>

          <div className="col-span-full w-full">
            <div className="mt-2 w-full flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
              <div className="text-center">
                {blobUrl ? (
                  <div>
                    <img
                      src={blobUrl}
                      alt="attractor image url"
                      className="w-max h-64 object-cover rounded-lg"
                    />
                  </div>
                ) : (
                  <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                )}
                <div className="mt-4 flex text-sm text-center leading-6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="w-max flex items-center cursor-pointer rounded-md font-semibold text-primary-600 focus-within:outline-none"
                  >
                    <span className="flex flex-between gap-2">
                      <CloudArrowUpIcon className="h-6 w-6 text-primary-600" />
                      <p> Click here to upload a file</p>
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange as any}
                    />
                  </label>
                </div>
                <p className="mt-1 text-xs leading-5 text-gray-600">
                  This is optional
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-600">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            </div>

            <div className="p-2 my-2 flex flex-col gap-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-400">
                Display Text
              </label>
              <CustomInput
                value={data.displayText}
                onChange={(e) =>
                  setData((prevData: any) => ({
                    ...prevData,
                    displayText: e.target.value,
                  }))
                }
                type="text"
                placeholder="Display text"
              />

              {data.attachmentType === 'WEB_URL' && (
                <>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-400">
                    External Link Url
                  </label>
                  <CustomInput
                    value={data.displayLinkUrl}
                    onChange={(e) =>
                      setData((prevData: any) => ({
                        ...prevData,
                        displayLinkUrl: e.target.value,
                      }))
                    }
                    type="text"
                    placeholder="Link Url"
                  />
                </>
              )}
            </div>
            <div className="my-2 flex flex-row gap-2 justify-between">
              <button
                disabled={loading}
                onClick={handleSubmit}
                type="button"
                className="w-full rounded-md bg-primary-900 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 "
                style={{
                  background: primary,
                }}
              >
                {'Create Link'}
              </button>
              <button
                disabled={loading}
                onClick={() => {
                  navigate('/mobile-about-us');
                }}
                type="button"
                className="w-full rounded-md bg-gray-400 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-300"
              >
                {'Cancel'}
              </button>
            </div>
            {error && (
              <div>
                <ErrorAlert message={error} />
              </div>
            )}
            {loading && (
              <div>
                <PELinearProgressWithLabel value={percentCompleted} />
              </div>
            )}
            {success && (
              <div>
                <SuccessAlert message="Link created successfully" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkCreator;
